/**
 * 선생님 live 수신(consume) 요청 중복 방지 ... 20261028 (web-relay R-207)
 *
 * 선생님이 송출을 다시 시작하면 서버가 newProducer를 연달아 두 번 보낸다.
 * 두 번째가 받기 연결 생성 직후에 오면 같은 종류(video 등) consume가 두 번 나가고,
 * 서버는 나중 consumer로 덮어써 화면에 붙은 첫 consumer가 paused로 남는다(검은 화면).
 *
 * 규칙 (milestone_41 §3.15·§3.17):
 *  - live consume 전송 시 종류별 ticket을 만든다. 같은 종류 ticket이 대기 중이면 전송하지 않는다.
 *  - ticket은 그 응답의 consumeAfter 처리가 끝났을 때(성공·실패·예외 포함) 또는 받기 연결 세대 초기화 때만 풀린다.
 *  - 시간 경과로는 풀지 않는다. 10초는 지연 경고 로그에만 쓴다.
 *  - 전송 함수가 동기 예외를 내면 자기 ticket을 즉시 취소한다.
 *  - handsup 요청과 SmartTV 페이지는 검사하지 않는다(기존 동작 유지).
 *  - 서버 정상 응답에는 requestId가 없으므로, 세대 초기화 뒤 늦게 온 응답은 기존처럼 그대로 처리한다.
 */

const LIVE_CONSUME_DELAY_WARN_MS = 10000;

const liveConsumeInfo = {
    gen: 0,
    seq: 0,
    pending: {}     // mediaKind => ticket
};

const getNow = () => Date.now();

/**
 * 자기 ticket일 때만 해제한다.
 */
const releaseLiveConsumeTicket = (ticket, reason) => {
    if (liveConsumeInfo.pending[ticket.mediaKind] === ticket) {
        delete liveConsumeInfo.pending[ticket.mediaKind];
        if (reason !== "consume-after-done") {
            console.log(`[LiveConsumeGuard] release - reason[${reason}], mediaKind[${ticket.mediaKind}], seq[${ticket.seq}]`);
        }
    }
};

/**
 * live consume 전송. 같은 종류가 대기 중이면 전송하지 않는다.
 *
 * @param {String} roomKind - "live" 또는 "handsup"
 * @param {String} mediaKind - "video" | "audio" | "mic"
 * @param {String} requestId - 추적용 요청 ID
 * @param {Boolean} isSmartTVPage - SmartTV 페이지 여부 (true면 검사하지 않음)
 * @param {Function} send - 실제 전송 함수
 * @returns {Boolean} 전송했으면 true
 */
export const sendLiveConsumeOnce = (roomKind, mediaKind, requestId, isSmartTVPage, send) => {
    if (roomKind !== "live" || isSmartTVPage) {
        send();
        return true;
    }

    const pendingTicket = liveConsumeInfo.pending[mediaKind];
    if (pendingTicket) {
        const elapsed = getNow() - pendingTicket.at;
        if (elapsed >= LIVE_CONSUME_DELAY_WARN_MS) {
            console.warn(`[LiveConsumeGuard] live consume 응답 지연 - mediaKind[${mediaKind}], elapsed[${elapsed}ms], pendingSeq[${pendingTicket.seq}], gen[${pendingTicket.gen}]`);
        }
        console.log(`[LiveConsumeGuard] 같은 종류 consume 대기 중이라 요청하지 않음 - mediaKind[${mediaKind}], requestId[${requestId}], pendingSeq[${pendingTicket.seq}], gen[${pendingTicket.gen}]`);
        return false;
    }

    const ticket = {
        mediaKind,
        gen: liveConsumeInfo.gen,
        seq: ++liveConsumeInfo.seq,
        at: getNow(),
        requestId
    };
    liveConsumeInfo.pending[mediaKind] = ticket;

    try {
        send();
    } catch (err) {
        // 확실한 전송 전 예외는 자기 ticket만 즉시 취소한다.
        releaseLiveConsumeTicket(ticket, "send-exception");
        throw err;
    }

    return true;
};

/**
 * consume 응답 처리 감싸기. live 응답이면 진입 시 잡은 ticket을 처리 완료 후 해제한다.
 *
 * @param {JSON} rtnVal - 서버 consume 응답
 * @param {Function} handler - 기존 consumeAfter
 */
export const runLiveConsumeAfter = async (rtnVal, handler) => {
    const roomKind = rtnVal ? rtnVal.roomKind : undefined;
    const mediaKind = rtnVal ? rtnVal.mediaKind : undefined;
    const ticket = roomKind === "live" ? liveConsumeInfo.pending[mediaKind] : undefined;

    if (roomKind === "live" && !ticket) {
        console.log(`[LiveConsumeGuard] 대기 ticket 없는 live consume 응답(기존대로 처리) - mediaKind[${mediaKind}], gen[${liveConsumeInfo.gen}]`);
    }

    try {
        return await handler(rtnVal);
    } finally {
        if (ticket) {
            releaseLiveConsumeTicket(ticket, "consume-after-done");
        }
    }
};

/**
 * 받기 연결 정리/생성 시 세대를 올리고 대기 ticket을 모두 버린다.
 *
 * @param {String} reason - 로그용 사유
 */
export const resetLiveConsume = (reason) => {
    liveConsumeInfo.gen++;
    const kinds = Object.keys(liveConsumeInfo.pending);
    liveConsumeInfo.pending = {};
    if (kinds.length > 0) {
        console.log(`[LiveConsumeGuard] reset - reason[${reason}], gen[${liveConsumeInfo.gen}], dropped[${kinds.join(",")}]`);
    }
};
